import { useState, useEffect, useCallback } from 'react';
import { TicketFavorito, FavoritoToggleResponse, FavoritoStatusResponse } from '@/types';
import { FavoritosService } from '@/services';

interface UseFavoritosReturn {
  favoritos: TicketFavorito[];
  loading: boolean;
  error: string | null;
  isFavorito: (ticketId: number) => boolean;
  toggleFavorito: (ticketId: number) => Promise<FavoritoToggleResponse>;
  verificarFavorito: (ticketId: number) => Promise<boolean>;
  atualizarFavoritos: () => Promise<void>;
  adicionarFavorito: (ticketId: number) => Promise<TicketFavorito>;
  removerFavorito: (favoritoId: number) => Promise<void>;
}

export const useFavoritos = (): UseFavoritosReturn => {
  const [favoritos, setFavoritos] = useState<TicketFavorito[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Carregar favoritos do usuário
  const atualizarFavoritos = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const favoritosData = await FavoritosService.getFavoritos();
      setFavoritos(favoritosData);
    } catch (err: any) {
      console.error('Erro ao carregar favoritos:', err);
      setError(err.message || 'Erro ao carregar favoritos');
      setFavoritos([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Verificar se um ticket específico é favorito
  const isFavorito = useCallback(
    (ticketId: number): boolean => {
      return favoritos.some(fav => fav.ticket_id === ticketId);
    },
    [favoritos]
  );

  // Toggle favorito (adicionar/remover)
  const toggleFavorito = useCallback(
    async (ticketId: number): Promise<FavoritoToggleResponse> => {
      try {
        setError(null);
        const response = await FavoritosService.toggleFavorito(ticketId);
        
        if (response.favorito) {
          // Adicionado aos favoritos
          if (response.data) {
            setFavoritos(prev => [...prev, response.data!]);
          }
        } else {
          // Removido dos favoritos
          setFavoritos(prev => prev.filter(fav => fav.ticket_id !== ticketId));
        }
        
        return response;
      } catch (err: any) {
        console.error('Erro ao alterar favorito:', err);
        setError(err.message || 'Erro ao alterar favorito');
        throw err;
      }
    },
    []
  );

  // Verificar status de favorito de um ticket específico
  const verificarFavorito = useCallback(
    async (ticketId: number): Promise<boolean> => {
      try {
        const response = await FavoritosService.verificarFavorito(ticketId);
        return response.favorito;
      } catch (err: any) {
        console.error('Erro ao verificar favorito:', err);
        return false;
      }
    },
    []
  );

  // Adicionar favorito
  const adicionarFavorito = useCallback(
    async (ticketId: number): Promise<TicketFavorito> => {
      try {
        setError(null);
        const novoFavorito = await FavoritosService.adicionarFavorito(ticketId);
        setFavoritos(prev => [...prev, novoFavorito]);
        return novoFavorito;
      } catch (err: any) {
        console.error('Erro ao adicionar favorito:', err);
        setError(err.message || 'Erro ao adicionar favorito');
        throw err;
      }
    },
    []
  );

  // Remover favorito
  const removerFavorito = useCallback(
    async (favoritoId: number): Promise<void> => {
      try {
        setError(null);
        await FavoritosService.removerFavorito(favoritoId);
        setFavoritos(prev => prev.filter(fav => fav.id !== favoritoId));
      } catch (err: any) {
        console.error('Erro ao remover favorito:', err);
        setError(err.message || 'Erro ao remover favorito');
        throw err;
      }
    },
    []
  );

  // Carregar favoritos na inicialização
  useEffect(() => {
    atualizarFavoritos();
  }, [atualizarFavoritos]);

  return {
    favoritos,
    loading,
    error,
    isFavorito,
    toggleFavorito,
    verificarFavorito,
    atualizarFavoritos,
    adicionarFavorito,
    removerFavorito,
  };
};

export default useFavoritos;